import { createClient } from "@supabase/supabase-js";

// Intercept Strava OAuth ?code= BEFORE createClient() runs.
// ES module imports are hoisted, so this file executes before any App.js code.
// Supabase's detectSessionInUrl reads window.location at createClient() time —
// if we don't strip the Strava ?code= first, it fires a spurious auth event.
(function() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const scope = params.get("scope") || "";
    if (code && scope.includes("activity:read")) {
      sessionStorage.setItem("strava_pending_code", code);
      window.history.replaceState({}, "", window.location.pathname);
    }
  } catch(e) {}
})();

const url  = process.env.REACT_APP_SUPABASE_URL;
const anon = process.env.REACT_APP_SUPABASE_ANON;

export const supabase = (url && anon) ? createClient(url, anon, {
  auth: {
    detectSessionInUrl: true,
    flowType: "pkce",
  }
}) : null;

export const db = supabase ? {
  getProfile:   (uid) => supabase.from("profiles").select("*").eq("id", uid).single().then(r => r.data),
  updateStravaTokens: (uid, t) => supabase.from("profiles").update({
    strava_access_token:  t.strava_access_token,
    strava_refresh_token: t.strava_refresh_token,
    strava_token_expires: t.strava_token_expires,
    strava_athlete_id:    t.strava_athlete_id,
  }).eq("id", uid),
  upsertProfile:(uid, p) => {
    // Only include defined fields to avoid overwriting with undefined
    const row = {};
    const set = (col, val) => { if (val !== undefined) row[col] = val; };
    set("prenom",             p.prenom);
    set("nom",                p.nom);
    set("age",                p.age);
    set("sexe",               p.sexe);
    set("taille",             p.taille);
    set("poids",              p.poids);
    set("masse_graisseuse",   p.masseGraisseuse);
    set("travail",            p.travail);
    set("stress_habituel",    p.stressHabituel);
    set("sommeil_cible",      p.sommeilCible);
    set("fc_max",             p.fc_max);
    set("fc_repos",           p.fc_repos);
    set("vma",                p.vma);
    set("objectif_principal", p.objectifPrincipal);
    set("objectif_chrono",    p.objectifChrono);
    set("objectif_date",      p.objectifDate);
    set("volume_cible",       p.volumeCible);
    set("experience_course",  p.experienceCourse);
    set("niveau_activite",    p.niveauActivite);
    set("onboarded",          p.onboarded);
    set("strava_athlete_id",  p.strava_athlete_id);
    set("strava_access_token",p.strava_access_token);
    set("strava_refresh_token",p.strava_refresh_token);
    set("strava_token_expires",p.strava_token_expires);
    return supabase.from("profiles").update(row).eq("id", uid)
      .then(r => { if (r.error) console.error("[upsertProfile]", r.error); return r; });
  },
  getRuns:      (uid) => supabase.from("runs").select("*").eq("user_id", uid).then(r => { if (r.error) console.error("[getRuns]", r.error); return r.data; }),
  addRun:       (uid, r) => { const { id: _, ...rest } = r; return supabase.from("runs").insert({ ...rest, user_id: uid }).select().single().then(r => { if (r.error) console.error("[addRun]", r.error); return r.data; }); },
  updateRun:    (uid, r) => supabase.from("runs").update({ ...r }).eq("id", r.id).eq("user_id", uid).then(r => { if (r.error) console.error("[updateRun]", r.error); }),
  deleteRun:    (uid, id) => supabase.from("runs").delete().eq("id", id).eq("user_id", uid),

  // Race mappers: JS uses camelCase, DB uses snake_case for complex fields
  getRaces:     (uid) => supabase.from("races").select("*").eq("user_id", uid).then(r => {
    if (r.error) console.error("[getRaces]", r.error);
    return (r.data || []).map(row => ({
      ...row,
      isTrail:       row.is_trail      ?? false,
      courseProfile: row.course_profile ?? null,
      trailData:     row.trail_data     ?? null,
      gpxData:       row.gpx_data       ?? null,
    }));
  }),
  addRace: (uid, r) => {
    const { id: _, isTrail, courseProfile, trailData, gpxData, ...rest } = r;
    const row = { ...rest, user_id: uid, is_trail: isTrail ?? false, course_profile: courseProfile ?? null, trail_data: trailData ?? null, gpx_data: gpxData ?? null };
    return supabase.from("races").insert(row).select().single().then(r => {
      if (r.error) console.error("[addRace]", r.error);
      if (!r.data) return null;
      return { ...r.data, isTrail: r.data.is_trail, courseProfile: r.data.course_profile, trailData: r.data.trail_data };
    });
  },
  updateRace: (uid, r) => {
    const { isTrail, courseProfile, trailData, gpxData, ...rest } = r;
    const row = { ...rest, is_trail: isTrail ?? false, course_profile: courseProfile ?? null, trail_data: trailData ?? null, gpx_data: gpxData ?? null };
    return supabase.from("races").update(row).eq("id", r.id).eq("user_id", uid).then(r => { if (r.error) console.error("[updateRace]", r.error); return r.data; });
  },
  deleteRace:   (uid, id) => supabase.from("races").delete().eq("id", id).eq("user_id", uid),

  // Race book community contributions — readable by all, writable by auth users
  getContributions: (raceId) => supabase.from("race_contributions").select("*").eq("race_id", raceId).order("created_at", { ascending: false }).then(r => { if (r.error) console.error("[getContributions]", r.error); return r.data || []; }),
  addContribution: (uid, c) => {
    const { id: _, ...rest } = c;
    return supabase.from("race_contributions").insert({ ...rest, user_id: uid }).select().single().then(r => { if (r.error) console.error("[addContribution]", r.error); return r.data; });
  },
  getSleep:     (uid) => supabase.from("sleep").select("*").eq("user_id", uid).then(r => r.data),
  addSleep:     (uid, s) => { const { id: _, ...rest } = s; return supabase.from("sleep").insert({ ...rest, user_id: uid }).select().single().then(r => r.data); },
  updateSleep:  (uid, s) => supabase.from("sleep").update({ ...s }).eq("id", s.id).eq("user_id", uid),
  deleteSleep:  (uid, id) => supabase.from("sleep").delete().eq("id", id).eq("user_id", uid),
  getHrv:       (uid) => supabase.from("hrv").select("*").eq("user_id", uid).then(r => r.data),
  addHrv:       (uid, h) => { const { id: _, ...rest } = h; return supabase.from("hrv").insert({ ...rest, user_id: uid }).select().single().then(r => r.data); },
  updateHrv:    (uid, h) => supabase.from("hrv").update({ ...h }).eq("id", h.id).eq("user_id", uid),
  deleteHrv:    (uid, id) => supabase.from("hrv").delete().eq("id", id).eq("user_id", uid),
  getWellness:  (uid) => supabase.from("wellness").select("*").eq("user_id", uid).then(r => r.data),
  addWellness:  (uid, w) => { const { id: _, ...rest } = w; return supabase.from("wellness").insert({ ...rest, user_id: uid }).select().single().then(r => r.data); },
  updateWellness:(uid, w) => supabase.from("wellness").update({ ...w }).eq("id", w.id).eq("user_id", uid),
  deleteWellness:(uid, id) => supabase.from("wellness").delete().eq("id", id).eq("user_id", uid),
  getEquipment: (uid) => supabase.from("equipment").select("*").eq("user_id", uid).then(r => r.data),
  addEquipment: (uid, e) => { const { id: _, ...rest } = e; return supabase.from("equipment").insert({ ...rest, user_id: uid }).select().single().then(r => r.data); },
  updateEquipment:(uid, e) => supabase.from("equipment").update({ ...e }).eq("id", e.id).eq("user_id", uid),
  deleteEquipment:(uid, id) => supabase.from("equipment").delete().eq("id", id).eq("user_id", uid),
} : null;